import {SwipeEvent, useSwipe} from 'ag-swipe-react';
import React, {useCallback, useMemo, useRef} from 'react';
import {ReactZoomPanPinchRef, TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import cn from 'classnames';

import {useUI} from 'coreHooks';
import {getAssetUrl} from 'coreUtils/adapters/sdk';

import {useCarouselControllerContext} from 'brandComponents/Carousel';
import Icon from 'brandComponents/Icon';

import {IZoomablePictureProps} from './IPicture';
import Picture from './Picture';

import styles from './ZoomablePicture.module.scss';

const ZoomablePicture = ({ref, ...args}: IZoomablePictureProps) => {
	const {
		closeClassName,
		isPinchToZoomEnabled = false,
		onClick,
		index,
		zoomOnClickEnabled = false,
		stepZoomOnClickEnabled = false,
		isChangeSlideAtSwipeEnabled = true,
		recenterOnChange = false,
		showPlaceholder,
		initialScale = 2,
		maxZoomStep = 3,
		zoomStep = 1,
	} = args;
	const zoomUI = useUI('pdpZoom');
	const stepZoomed = useRef(0);
	const isZoomActive = zoomUI.isActive;
	const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
	const SWIPE_DISTANCE = 200;
	const {swiperRef} = useCarouselControllerContext(true);
	const cursorZoomPlus = useMemo(() => getAssetUrl('static/img/zoom-plus.svg'), []);
	const cursorZoomMinus = useMemo(() => getAssetUrl('static/img/zoom-minus.svg'), []);

	const swipeElement = useSwipe({
		onSwipeEnd: (event: SwipeEvent) => {
			if (!swiperRef || !isChangeSlideAtSwipeEnabled || event.direction !== 'y') {
				return;
			}

			if (event.distance > SWIPE_DISTANCE) {
				swiperRef.current.slidePrev();
			} else if (event.distance < -SWIPE_DISTANCE) {
				swiperRef.current.slideNext();
			}
		},
	});

	if (recenterOnChange && transformWrapperRef?.current) {
		const {centerView} = transformWrapperRef.current;

		centerView && centerView(initialScale, 0);
	}

	const setCursorStyle = useCallback((cursorStyle) => {
		if (!swiperRef?.current) {
			return;
		}

		const indexCurrentSlide = swiperRef.current.realIndex;
		const currentSlide = swiperRef.current.slides[indexCurrentSlide];

		currentSlide.style.cursor = `url(${cursorStyle}) 10 10, pointer`;
	}, [swiperRef]);

	if (stepZoomOnClickEnabled) {
		setCursorStyle(cursorZoomPlus);
	}

	const handleClick = useCallback((e) => {
		if (stepZoomOnClickEnabled && transformWrapperRef?.current) {
			const {zoomOut, zoomIn} = transformWrapperRef.current;
			const updatedZoomedTime = stepZoomed.current + 1;

			if (updatedZoomedTime >= maxZoomStep) {
				zoomOut(maxZoomStep);
				stepZoomed.current = 0;
				setCursorStyle(cursorZoomPlus);
			} else {
				zoomIn(zoomStep);
				stepZoomed.current = updatedZoomedTime;

				if (stepZoomed.current >= maxZoomStep - 1) {
					setCursorStyle(cursorZoomMinus);
				}
			}
		}

		return onClick && onClick(e);
	}, [stepZoomOnClickEnabled, onClick, maxZoomStep, zoomStep, setCursorStyle]);

	const centerViewOnImageLoaded = useCallback(() => {
		if (transformWrapperRef?.current) {
			const {centerView, setTransform, instance} = transformWrapperRef.current;

			if (zoomOnClickEnabled && zoomUI.imageIndex === index && instance?.wrapperComponent) {
				const MAX_SCALE = 2;
				const wrapper = instance.wrapperComponent;
				const x = wrapper?.getBoundingClientRect().left;
				const y = wrapper?.getBoundingClientRect().top;
				const coordinates = {
					x: x - zoomUI.clickedCoordinates.x,
					y: y - zoomUI.clickedCoordinates.y,
				};

				setTransform(
					(coordinates.x + wrapper?.clientWidth / (MAX_SCALE * 2)) * MAX_SCALE,
					(coordinates.y + wrapper?.clientHeight / (MAX_SCALE * 2)) * MAX_SCALE,
					MAX_SCALE,
				);
			} else {
				centerView && centerView(initialScale, 0);
			}
		}
	}, [zoomOnClickEnabled, zoomUI?.clickedCoordinates, index, initialScale]);

	return isZoomActive && (isPinchToZoomEnabled || stepZoomOnClickEnabled) ? (
		<TransformWrapper
			initialScale={initialScale}
			limitToBounds={true}
			ref={transformWrapperRef}
			minScale={initialScale}
		>
			<>
				<div onClick={handleClick} onTouchEnd={handleClick} className={cn(closeClassName, styles['zoom-close'])}>
					<Icon name="close" width={12} height={12} />
				</div>

				<TransformComponent
					contentStyle={{
						minWidth: `${100 / initialScale}%`,
						minHeight: `${100 / initialScale}%`,
					}}
				>
					<div onClick={handleClick} ref={swipeElement}>
						<Picture
							{...args}
							onImageLoaded={centerViewOnImageLoaded}
							ref={ref}
							noResize={true}
							onClick={undefined}
							showSkeleton={false}
							showPlaceholder={showPlaceholder}
						/>
					</div>
				</TransformComponent>
			</>
		</TransformWrapper>
	) : (
		<Picture {...args} ref={ref} showSkeleton={false} showPlaceholder={showPlaceholder} />
	);
};

export default ZoomablePicture;
